import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import Table from 'components/ui/Table';
import ItemCountDisplay from 'components/ui/ItemCountDisplay';
import SearchInput from 'components/Search/SearchInput';

import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { orderSelectors, orderThunks } from 'store/reducers/orderSlice';
import { CustomerSearchItem } from 'returnCenter-api';

import useDebouncedSearch from 'hooks/useDebouncedSearch';
import SearchResults from './SearchResults';

type CustomerSearchStepProps = {
    onCustomerSelect: (customer: CustomerSearchItem) => void;
};

const CustomerSearchStep = ({ onCustomerSelect }: CustomerSearchStepProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [query, setQuery] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const results = useAppSelector(orderSelectors.selectCustomerSearchResults);
    const loading = useAppSelector(orderSelectors.selectCustomerSearchLoading);
    const error = useAppSelector(orderSelectors.selectCustomerSearchError);

    const handleSearch = useCallback(() => {
        if (!query.trim()) return;
        return dispatch(orderThunks.searchCustomers({ search: query }));
    }, [dispatch, query]);

    const { searchNow } = useDebouncedSearch(handleSearch, query, 300, 1);

    const handleClear = useCallback(() => {
        setQuery('');
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.focus();
        }
    }, []);

    return (
        <StepContent column $grow={1}>
            <SearchInput
                inputRef={inputRef}
                containerRef={containerRef}
                placeholder={t('searchCustomerPlaceholder')}
                searchString={query}
                onSearch={(e) => setQuery(e.target.value)}
                onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        searchNow();
                    }
                }}
                onClear={handleClear}
                onIconClick={searchNow}
            />
            <ResultsContainer align="center" gap="1rem" column $grow={1}>
                <SearchResults loading={loading} error={error} hasResults={results.length > 0}>
                    <TableWrapper column gap="1rem" $grow={1}>
                        <ItemCountDisplay
                            title={t('customer')}
                            count={results.length}
                            countLabel={t('items')}
                        />
                        <Table
                            className="f3-300"
                            bgColor="fillWhite"
                            cols={3}
                            headers={[t('customerName'), t('customerCode'), t('customerGroup')]}
                            rows={results.map((c: CustomerSearchItem) => ({
                                cells: [
                                    { el: <Text>{c.name}</Text> },
                                    { el: <Text>{c.customerCode}</Text> },
                                    { el: <Text>{c.groupCode}</Text> },
                                ],
                                onClick: () => onCustomerSelect(c),
                            }))}
                            arrow
                        />
                    </TableWrapper>
                </SearchResults>
            </ResultsContainer>
        </StepContent>
    );
};

export default CustomerSearchStep;

// --- Styled components ---

const StepContent = styled(Flex)`
    overflow: auto;
    margin-top: 0.5rem;
`;

const ResultsContainer = styled(Flex)`
    overflow: auto;
`;

const TableWrapper = styled(Flex)`
    width: 100%;
`;
